import React from "react";
import { createPortal } from "react-dom";
import styled, { keyframes } from "styled-components";
import {
  AlertTriangle,
  Clock,
  Camera,
  FolderOpen,
  X,
  Info,
  CheckCircle2,
} from "lucide-react";
import {
  ModalOverlay,
  ModalShell,
  ModalHero,
  HeroRow,
  HeroText,
  HeroBadge,
  ModalTitle,
  ModalSubtitle,
  CloseBtn,
  ModalBody,
  gradient,
} from "../../../molecules/Dashboard/EventCalendar/eventModalSharedStyles";
import { C2CSession } from "../../../../Redux/ApiCalls/Dashboard/C2CAPI";

export interface ExpiringSessionAlertItem {
  session: C2CSession;
  threshold: "24h" | "3h";
  expiryLabel: string;
}

interface ExpiryAlertModalProps {
  alerts: ExpiringSessionAlertItem[];
  onClose: () => void;
}

const pulse = keyframes`
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.85; transform: scale(0.98); }
`;

const SessionAlertCard = styled.div<{ $isUrgent: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 16px;
  border-radius: 14px;
  background: ${(p) => (p.$isUrgent ? "rgba(239, 68, 68, 0.04)" : "rgba(245, 158, 11, 0.04)")};
  border: 1px solid ${(p) => (p.$isUrgent ? "rgba(239, 68, 68, 0.22)" : "rgba(245, 158, 11, 0.22)")};
  transition: all 0.2s ease;
`;

const CardHeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
`;

const DeviceInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const DeviceIconWrap = styled.div<{ $isUrgent: boolean }>`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${(p) => (p.$isUrgent ? "rgba(239, 68, 68, 0.12)" : "rgba(245, 158, 11, 0.12)")};
  color: ${(p) => (p.$isUrgent ? "#ef4444" : "#d97706")};
`;

const DeviceMeta = styled.div`
  display: flex;
  flex-direction: column;
`;

const DeviceName = styled.span`
  font-family: Urbanist, sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: #171717;
`;

const DeviceBrand = styled.span`
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 500;
  color: rgba(23, 23, 23, 0.55);
`;

const ExpiryBadge = styled.div<{ $isUrgent: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 10px;
  border-radius: 8px;
  font-family: Urbanist, sans-serif;
  font-size: 12px;
  font-weight: 700;
  background: ${(p) => (p.$isUrgent ? "#ef4444" : "#f59e0b")};
  color: #ffffff;
  animation: ${pulse} 2s infinite ease-in-out;
`;

const MetaRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  color: rgba(23, 23, 23, 0.7);
`;

const MetaItem = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
`;

const NoticeBox = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 14px;
  border-radius: 12px;
  background: rgba(201, 77, 170, 0.06);
  border: 1px solid rgba(201, 77, 170, 0.15);
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: rgba(23, 23, 23, 0.72);
  line-height: 1.5;
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 16px 24px 24px;
`;

const ConfirmBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 44px;
  padding: 0 24px;
  border: none;
  border-radius: 12px;
  background: ${gradient};
  color: #fff;
  font-family: Urbanist, sans-serif;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 8px 20px rgba(201, 77, 170, 0.25);
  transition: all 0.15s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 10px 24px rgba(201, 77, 170, 0.32);
  }
`;

export const ExpiryAlertModal: React.FC<ExpiryAlertModalProps> = ({ alerts, onClose }) => {
  const hasUrgent = alerts.some((a) => a.threshold === "3h");

  return createPortal(
    <ModalOverlay id="c2c-expiry-alert-overlay" onClick={onClose}>
      <ModalShell style={{ maxWidth: 480 }} onClick={(e) => e.stopPropagation()}>
        <ModalHero
          style={{
            background: hasUrgent
              ? "linear-gradient(135deg, rgba(239,68,68,0.12) 0%, rgba(245,158,11,0.08) 100%)"
              : "linear-gradient(135deg, rgba(245,158,11,0.12) 0%, rgba(201,77,170,0.06) 100%)",
            borderBottom: hasUrgent
              ? "1px solid rgba(239,68,68,0.15)"
              : "1px solid rgba(245,158,11,0.15)",
          }}
        >
          <HeroRow>
            <HeroText>
              <HeroBadge
                style={{
                  color: hasUrgent ? "#ef4444" : "#d97706",
                  borderColor: hasUrgent ? "rgba(239,68,68,0.25)" : "rgba(245,158,11,0.25)",
                  background: hasUrgent ? "rgba(239,68,68,0.08)" : "rgba(245,158,11,0.08)",
                }}
              >
                <AlertTriangle size={11} />
                {hasUrgent ? "Urgent Session Alert" : "Session Expiration Notice"}
              </HeroBadge>
              <ModalTitle style={{ color: hasUrgent ? "#dc2626" : "#171717" }}>
                {hasUrgent ? "Session Expiry in Less Than 3 Hours" : "Session Expiry in Less Than 24 Hours"}
              </ModalTitle>
              <ModalSubtitle>
                {alerts.length === 1
                  ? "Your active Camera to Cloud session is about to expire."
                  : "The following Camera to Cloud sessions are about to expire."}
              </ModalSubtitle>
            </HeroText>
            <CloseBtn
              id="c2c-expiry-alert-close"
              type="button"
              onClick={onClose}
              aria-label="Close"
            >
              <X size={18} />
            </CloseBtn>
          </HeroRow>
        </ModalHero>

        <ModalBody style={{ padding: "20px 24px", display: "flex", flexDirection: "column", gap: 14 }}>
          {alerts.map(({ session, threshold, expiryLabel }) => {
            const isUrgent = threshold === "3h";
            return (
              <SessionAlertCard key={session.session_id} $isUrgent={isUrgent}>
                <CardHeaderRow>
                  <DeviceInfo>
                    <DeviceIconWrap $isUrgent={isUrgent}>
                      <Camera size={16} />
                    </DeviceIconWrap>
                    <DeviceMeta>
                      <DeviceName>{session.device?.name || "Camera Session"}</DeviceName>
                      <DeviceBrand>{session.device?.brand || "FTPS"}</DeviceBrand>
                    </DeviceMeta>
                  </DeviceInfo>
                  <ExpiryBadge $isUrgent={isUrgent}>
                    <Clock size={12} />
                    {expiryLabel}
                  </ExpiryBadge>
                </CardHeaderRow>

                <MetaRow>
                  <MetaItem>
                    <FolderOpen size={13} style={{ color: "#c94daa" }} />
                    <span>
                      <strong>Album:</strong> {session.album?.name || "—"}
                    </span>
                  </MetaItem>
                  <MetaItem>
                    <span>
                      <strong>Folder:</strong> {session.folder?.name || "—"}
                    </span>
                  </MetaItem>
                </MetaRow>
              </SessionAlertCard>
            );
          })}

          <NoticeBox>
            <Info size={16} style={{ color: "#c94daa", flexShrink: 0, marginTop: 1 }} />
            <span>
              When a session expires, active camera uploads will be disconnected. To continue uploading after expiry, create a new session from the dashboard.
            </span>
          </NoticeBox>
        </ModalBody>

        <ModalFooter>
          <ConfirmBtn id="c2c-expiry-alert-dismiss-btn" type="button" onClick={onClose}>
            <CheckCircle2 size={16} /> Got it
          </ConfirmBtn>
        </ModalFooter>
      </ModalShell>
    </ModalOverlay>,
    document.body
  );
};
